'use client'

import {
  Shield,
  Zap,
  TrendingUp,
  Palette,
  DollarSign,
  Eye,
  Users,
  MessageSquare,
  CheckCircle,
  ArrowRight,
  Gauge,
  Target,
  Activity,
  BarChart3,
} from 'lucide-react'

const brandColors = {
  primary: '#008dcc',
  accent: '#FDC304',
  pink: '#F90B8A',
  purple: '#8b5cf6',
  green: '#10b981',
  red: '#ef4444',
}

/**
 * Five-pillar AX metrics framework overview
 */
export function AXMetricsFrameworkDiagram() {
  const pillars = [
    {
      icon: Shield,
      name: 'Trust Calibration',
      color: brandColors.primary,
      metrics: [
        'Auto-approve rate',
        'Verification rate',
        'Trust recovery time',
      ],
      question: 'Do users trust appropriately?',
    },
    {
      icon: Zap,
      name: 'Conversation Efficiency',
      color: brandColors.accent,
      metrics: ['Goal completion', 'Turns-to-resolution', 'Containment rate'],
      question: 'Is friction minimized?',
    },
    {
      icon: TrendingUp,
      name: 'Autonomy Progression',
      color: brandColors.green,
      metrics: [
        'Level distribution',
        'Regression events',
        'Ceiling by task type',
      ],
      question: 'Is independence growing?',
    },
    {
      icon: Palette,
      name: 'Personality Consistency',
      color: brandColors.pink,
      metrics: ['Tone compliance', 'Vocabulary adherence', 'Context variation'],
      question: 'Is voice stable?',
    },
    {
      icon: DollarSign,
      name: 'Business Impact',
      color: brandColors.purple,
      metrics: ['Revenue correlation', 'Cost per resolution', 'Return rate'],
      question: 'Does it move outcomes?',
    },
  ]

  return (
    <div data-blog-layout='narrow'>
      <div className='bg-muted/50 rounded-2xl border border-border/60 p-6 md:p-8'>
        <div className='text-center mb-6'>
          <p className='text-sm font-semibold text-foreground/80 uppercase tracking-wider'>
            The AX Metrics Framework
          </p>
          <p className='text-xs text-muted-foreground mt-1'>
            Five pillars for measuring agentic experience quality
          </p>
        </div>

        <div className='grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3'>
          {pillars.map(pillar => {
            const Icon = pillar.icon
            return (
              <div
                key={pillar.name}
                className='bg-background rounded-xl border border-border/60 p-4 flex flex-col items-center text-center'
              >
                <div
                  className='w-10 h-10 rounded-lg flex items-center justify-center mb-3'
                  style={{ backgroundColor: `${pillar.color}15` }}
                >
                  <Icon className='w-5 h-5' style={{ color: pillar.color }} />
                </div>
                <p
                  className='text-xs font-bold mb-1'
                  style={{ color: pillar.color }}
                >
                  {pillar.name}
                </p>
                <p className='text-[10px] text-muted-foreground italic mb-3'>
                  {pillar.question}
                </p>
                <div className='space-y-1 w-full'>
                  {pillar.metrics.map(metric => (
                    <div key={metric} className='bg-muted rounded px-2 py-1'>
                      <p className='text-[10px] text-foreground/80'>{metric}</p>
                    </div>
                  ))}
                </div>
              </div>
            )
          })}
        </div>

        <div className='mt-5 bg-background rounded-xl border border-border/60 p-4'>
          <div className='flex items-center justify-center gap-2 flex-wrap'>
            {pillars.map((pillar, i) => (
              <div key={pillar.name} className='flex items-center gap-2'>
                <div
                  className='w-2 h-2 rounded-full'
                  style={{ backgroundColor: pillar.color }}
                />
                <span className='text-[10px] text-foreground/80'>
                  {pillar.name}
                </span>
                {i < pillars.length - 1 && (
                  <ArrowRight className='w-3 h-3 text-muted-foreground' />
                )}
              </div>
            ))}
          </div>
          <p className='text-[10px] text-muted-foreground text-center mt-2'>
            Pillars build on each other: trust enables autonomy, which drives
            business impact
          </p>
        </div>
      </div>
    </div>
  )
}

/**
 * Trust calibration progression showing auto-approve rates over sessions
 */
export function TrustCalibrationDiagram() {
  const stages = [
    {
      sessions: '<50',
      autoApprove: 20,
      verification: 80,
      label: 'Cautious',
      behavior: 'Approves each action individually',
      color: brandColors.red,
    },
    {
      sessions: '50-200',
      autoApprove: 28,
      verification: 65,
      label: 'Testing',
      behavior: 'Allows batches, spot-checks results',
      color: brandColors.accent,
    },
    {
      sessions: '200-500',
      autoApprove: 35,
      verification: 45,
      label: 'Building',
      behavior: 'Auto-approves routine, reviews novel',
      color: brandColors.primary,
    },
    {
      sessions: '750+',
      autoApprove: 42,
      verification: 30,
      label: 'Calibrated',
      behavior: 'Monitors and intervenes when needed',
      color: brandColors.green,
    },
  ]

  return (
    <div data-blog-layout='narrow'>
      <div className='bg-muted/50 rounded-2xl border border-border/60 p-6 md:p-8'>
        <div className='text-center mb-6'>
          <p className='text-sm font-semibold text-foreground/80 uppercase tracking-wider'>
            Trust Calibration Curve
          </p>
          <p className='text-xs text-muted-foreground mt-1'>
            Auto-approve rate progression over user sessions (Anthropic
            research)
          </p>
        </div>

        <div className='space-y-3'>
          {stages.map(stage => (
            <div
              key={stage.sessions}
              className='bg-background rounded-xl border border-border/60 p-4'
            >
              <div className='flex items-center justify-between mb-3'>
                <div className='flex items-center gap-3'>
                  <div
                    className='px-2 py-1 rounded-md text-[10px] font-bold'
                    style={{
                      backgroundColor: `${stage.color}15`,
                      color: stage.color,
                    }}
                  >
                    {stage.label}
                  </div>
                  <span className='text-xs text-muted-foreground'>
                    {stage.sessions} sessions
                  </span>
                </div>
                <span className='text-xs text-foreground/80'>
                  {stage.behavior}
                </span>
              </div>

              <div className='space-y-2'>
                <div>
                  <div className='flex items-center justify-between mb-1'>
                    <span className='text-[10px] text-muted-foreground'>
                      Auto-approve
                    </span>
                    <span
                      className='text-[10px] font-bold'
                      style={{ color: stage.color }}
                    >
                      {stage.autoApprove}%
                    </span>
                  </div>
                  <div className='h-2 bg-muted rounded-full overflow-hidden'>
                    <div
                      className='h-full rounded-full transition-all'
                      style={{
                        width: `${stage.autoApprove}%`,
                        backgroundColor: stage.color,
                      }}
                    />
                  </div>
                </div>

                <div>
                  <div className='flex items-center justify-between mb-1'>
                    <span className='text-[10px] text-muted-foreground'>
                      Verification rate
                    </span>
                    <span className='text-[10px] font-bold text-foreground/80'>
                      {stage.verification}%
                    </span>
                  </div>
                  <div className='h-2 bg-muted rounded-full overflow-hidden'>
                    <div
                      className='h-full rounded-full transition-all bg-foreground/20'
                      style={{ width: `${stage.verification}%` }}
                    />
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        <div
          className='mt-4 rounded-xl p-3 border'
          style={{
            backgroundColor: `${brandColors.green}08`,
            borderColor: `${brandColors.green}20`,
          }}
        >
          <div className='flex items-start gap-2'>
            <TrendingUp
              className='w-4 h-4 mt-0.5 flex-shrink-0'
              style={{ color: brandColors.green }}
            />
            <p className='text-[11px] text-foreground/80'>
              <span className='font-semibold'>Key insight:</span> Both
              interruptions and auto-approvals increase with experience. Users
              shift from "approve each action" to "let it work, step in when
              needed."
            </p>
          </div>
        </div>
      </div>
    </div>
  )
}

/**
 * Five levels of agent autonomy
 */
export function AutonomyLevelsDiagram() {
  const levels = [
    {
      level: 1,
      role: 'Operator',
      description: 'User directs everything, agent assists on demand',
      examples: 'ChatGPT Canvas, Copilot',
      userInvolvement: 95,
      color: brandColors.red,
      icon: Users,
    },
    {
      level: 2,
      role: 'Collaborator',
      description: 'Both plan and execute together with frequent interaction',
      examples: 'OpenAI Operator',
      userInvolvement: 70,
      color: brandColors.accent,
      icon: MessageSquare,
    },
    {
      level: 3,
      role: 'Consultant',
      description: 'Agent leads execution, user provides focused expertise',
      examples: 'Gemini Deep Research, GitHub Copilot Agent',
      userInvolvement: 40,
      color: brandColors.primary,
      icon: Eye,
    },
    {
      level: 4,
      role: 'Approver',
      description:
        'Agent operates independently, user approves high-stakes only',
      examples: 'SWE Agent, Devin',
      userInvolvement: 15,
      color: brandColors.purple,
      icon: CheckCircle,
    },
    {
      level: 5,
      role: 'Observer',
      description: 'Fully autonomous, user monitors through activity logs',
      examples: 'Voyager, The AI Scientist',
      userInvolvement: 5,
      color: brandColors.green,
      icon: Activity,
    },
  ]

  return (
    <div data-blog-layout='narrow'>
      <div className='bg-muted/50 rounded-2xl border border-border/60 p-6 md:p-8'>
        <div className='text-center mb-6'>
          <p className='text-sm font-semibold text-foreground/80 uppercase tracking-wider'>
            Five Levels of Agent Autonomy
          </p>
          <p className='text-xs text-muted-foreground mt-1'>
            Track which level users operate at to measure trust progression
          </p>
        </div>

        <div className='space-y-2'>
          {levels.map(level => {
            const Icon = level.icon
            return (
              <div
                key={level.level}
                className='bg-background rounded-xl border border-border/60 p-4'
              >
                <div className='flex items-start gap-3'>
                  <div
                    className='w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0'
                    style={{ backgroundColor: `${level.color}15` }}
                  >
                    <span
                      className='text-sm font-bold'
                      style={{ color: level.color }}
                    >
                      {level.level}
                    </span>
                  </div>

                  <div className='flex-1 min-w-0'>
                    <div className='flex items-center gap-2 mb-1'>
                      <span className='text-xs font-bold text-foreground'>
                        User as {level.role}
                      </span>
                      <Icon
                        className='w-3.5 h-3.5'
                        style={{ color: level.color }}
                      />
                    </div>
                    <p className='text-[11px] text-foreground/80 mb-2'>
                      {level.description}
                    </p>

                    <div className='flex items-center gap-3'>
                      <div className='flex-1'>
                        <div className='h-1.5 bg-muted rounded-full overflow-hidden'>
                          <div
                            className='h-full rounded-full'
                            style={{
                              width: `${level.userInvolvement}%`,
                              backgroundColor: level.color,
                            }}
                          />
                        </div>
                      </div>
                      <span className='text-[10px] text-muted-foreground whitespace-nowrap'>
                        {level.userInvolvement}% user involvement
                      </span>
                    </div>

                    <p className='text-[10px] text-muted-foreground mt-1.5 italic'>
                      Examples: {level.examples}
                    </p>
                  </div>
                </div>
              </div>
            )
          })}
        </div>

        <div
          className='mt-4 rounded-xl p-3 border'
          style={{
            backgroundColor: `${brandColors.primary}08`,
            borderColor: `${brandColors.primary}20`,
          }}
        >
          <p className='text-[11px] text-foreground/80 text-center'>
            <span className='font-semibold'>Track progression:</span> A healthy
            agent shows users migrating from Level 1-2 toward Level 3-4 within
            30-90 days of onboarding
          </p>
        </div>
      </div>
    </div>
  )
}

/**
 * AX Metrics Dashboard overview showing the three review cadences
 */
export function AXMetricsDashboardDiagram() {
  const cadences = [
    {
      name: 'Real-Time Alerts',
      audience: 'Engineering',
      color: brandColors.red,
      icon: Activity,
      metrics: [
        'Boundary violations',
        'Error rate spikes',
        'Containment drops',
        'Personality below 85%',
      ],
      frequency: 'Immediate',
    },
    {
      name: 'Weekly Review',
      audience: 'Product',
      color: brandColors.primary,
      icon: BarChart3,
      metrics: [
        'Trust calibration trends',
        'Conversation efficiency',
        'Personality consistency',
        'Autonomy level shifts',
      ],
      frequency: 'Every Monday',
    },
    {
      name: 'Monthly Strategic',
      audience: 'Leadership',
      color: brandColors.purple,
      icon: Target,
      metrics: [
        'Revenue correlation',
        'Cost per resolution',
        'Cohort analysis',
        'Autonomy curves',
      ],
      frequency: 'First of month',
    },
  ]

  return (
    <div data-blog-layout='narrow'>
      <div className='bg-muted/50 rounded-2xl border border-border/60 p-6 md:p-8'>
        <div className='text-center mb-6'>
          <p className='text-sm font-semibold text-foreground/80 uppercase tracking-wider'>
            AX Metrics Dashboard
          </p>
          <p className='text-xs text-muted-foreground mt-1'>
            Three cadences, three audiences, right signals at the right time
          </p>
        </div>

        <div className='grid grid-cols-1 md:grid-cols-3 gap-3'>
          {cadences.map(cadence => {
            const Icon = cadence.icon
            return (
              <div
                key={cadence.name}
                className='bg-background rounded-xl border border-border/60 p-4'
              >
                <div className='flex items-center gap-2 mb-3'>
                  <div
                    className='w-8 h-8 rounded-lg flex items-center justify-center'
                    style={{ backgroundColor: `${cadence.color}15` }}
                  >
                    <Icon
                      className='w-4 h-4'
                      style={{ color: cadence.color }}
                    />
                  </div>
                  <div>
                    <p
                      className='text-xs font-bold'
                      style={{ color: cadence.color }}
                    >
                      {cadence.name}
                    </p>
                    <p className='text-[10px] text-muted-foreground'>
                      {cadence.audience}
                    </p>
                  </div>
                </div>

                <div className='space-y-1.5 mb-3'>
                  {cadence.metrics.map(metric => (
                    <div key={metric} className='flex items-center gap-2'>
                      <Gauge
                        className='w-3 h-3 flex-shrink-0'
                        style={{ color: cadence.color }}
                      />
                      <span className='text-[10px] text-foreground/80'>
                        {metric}
                      </span>
                    </div>
                  ))}
                </div>

                <div
                  className='rounded-lg px-2 py-1 text-center'
                  style={{ backgroundColor: `${cadence.color}10` }}
                >
                  <span
                    className='text-[10px] font-semibold'
                    style={{ color: cadence.color }}
                  >
                    {cadence.frequency}
                  </span>
                </div>
              </div>
            )
          })}
        </div>

        <div className='mt-4 bg-background rounded-xl border border-border/60 p-3'>
          <p className='text-[11px] text-foreground/80 text-center'>
            <span className='font-semibold'>Implementation order:</span> Start
            with trust basics (week 1), add efficiency (weeks 2-3), build
            autonomy tracking (weeks 4-6), refine with personality and business
            impact (month 2+)
          </p>
        </div>
      </div>
    </div>
  )
}
